import {
  customElement,
  type TemplateResult,
  state,
  html,
  property,
  css,
  cssManager,
} from '@design.estate/dees-element';
import * as domtools from '@design.estate/dees-domtools';
import { DeesInputBase } from '../dees-input-base/dees-input-base.js';

import { demoFunc }  from './dees-input-typelist.demo.js';
import { themeDefaultStyles } from '../../00theme.js';
import '../../00group-utility/dees-icon/dees-icon.js';

@customElement('dees-input-typelist')
export class DeesInputTypelist extends DeesInputBase<DeesInputTypelist> {
  public static inputLayout = 'compound' as const;
  public static demo = demoFunc;
  public static demoGroups = ['Input'];


  // INSTANCE

  @property({ type: Array })
  accessor value: string[] = [];

  @state()
  accessor inputValue: string = '';


  public static styles = [
    themeDefaultStyles,
    ...DeesInputBase.baseStyles,
    cssManager.defaultStyles,
    css`
      :host {
        color: var(--dees-color-text-primary);
        --typelist-hover-filter: brightness(1.05);
      }
      :host([gobright]) {
        --typelist-hover-filter: brightness(0.98);
      }
      .mainbox {
        border-radius: var(--dees-radius-lg);
        background: var(--dees-color-control-bg);
        overflow: hidden;
        border-top: 1px solid var(--dees-color-border-default);
        border-bottom: 1px solid var(--dees-color-border-default);
        border-right: 1px solid var(--dees-color-border-default);
        border-left: 1px solid var(--dees-color-border-default);
        box-shadow: none;
        position: relative;
      }
      
      .mainbox:hover {
        border-color: var(--dees-color-border-strong);
      }
      
      .mainbox:focus-within {
        outline: 2px solid var(--dees-color-focus-ring);
        outline-offset: 1px;
      }

      .tags {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
        padding: 8px;
        cursor: default;
      }

      .notags {
        text-align: center;
        color: var(--dees-color-text-muted);
        font-size: 13px;
      }

      input {
        display: block;
        box-sizing: border-box;
        background: transparent;
        width: 100%;
        outline: none;
        border: none;
        color: inherit;
        padding: 0px 16px;
        overflow: hidden;
        font: inherit;
        min-height: var(--dees-input-height);
        border-top: 1px solid var(--dees-color-border-default);
      }

      input:focus {
        background: transparent;
      }
      
      input::placeholder {
        color: var(--dees-color-text-muted);
      }

      .tag {
        display: inline-flex;
        align-items: center;
        max-width: 100%;
        overflow-wrap: anywhere;
        background: var(--dees-color-fill-secondary);
        color: var(--dees-color-text-secondary);
        padding: 0 0 0 8px;
        border-radius: var(--dees-radius-sm);
        margin: 0;
        font-size: 12px;
        font-weight: 500;
        border: none;
      }
      
      .tag:hover {
        background: var(--dees-color-fill-secondary);
      }

      .tag .remove {
        margin-left: 8px;
        cursor: pointer;
        opacity: 0.7;
        font-weight: 700;
        font-size: 16px;
        line-height: 1;
        transition: opacity 0.2s;
      }

      .tag .remove:hover {
        opacity: 1;
        color: var(--dees-color-text-error);
      }
      
      /* Disabled state */
      :host([disabled]) .mainbox {
        opacity: 0.6;
        cursor: not-allowed;
      }
      
      :host([disabled]) .tags {
        cursor: not-allowed;
      }
      
      :host([disabled]) .tag {
        pointer-events: none;
      }
      
      :host([disabled]) input {
        cursor: not-allowed;
        background: var(--dees-color-code-block-bg);
      }
    `,
  ];

  public render(): TemplateResult {
    return html`
      <div class="input-wrapper">
        ${this.renderLabel()}
        <div class="mainbox field-control">
          <div class="tags" @click=${() => {
            this.shadowRoot!.querySelector('input')!.focus();
          }}>
            ${this.value.length === 0
              ? html`<div class="notags">No tags yet</div>`
              : this.value.map(
                  (tag) => html`
                    <span class="tag">
                      ${tag}
                      <button type="button" class="remove input-action" aria-label=${`Remove ${tag}`} ?disabled=${this.disabled} @click=${(e: Event) => {
                        e.stopPropagation();
                        this.removeTag(tag);
                      }}><dees-icon icon="lucide:X" iconSize="12"></dees-icon></button>
                    </span>
                  `
                )}
          </div>
          <input
            type="text"
            aria-label=${this.label ? `Add to ${this.label}` : 'Add item'}
            placeholder="Type, press Enter to add it..."
            .value=${this.inputValue}
            @input=${(e: InputEvent) => {
              this.inputValue = (e.target as HTMLInputElement).value;
            }}
            @keydown=${(e: KeyboardEvent) => {
              if (e.key === 'Enter' && !e.isComposing && this.inputValue.trim()) {
                e.preventDefault();
                this.addTag(this.inputValue.trim());
              }
            }}
            .disabled=${this.disabled}
          />
        </div>
        ${this.renderDescription()}
      </div>
    `;
  }

  private addTag(tag: string) {
    if (this.disabled) return;
    if (!this.value.includes(tag)) {
      this.value = [...this.value, tag];
      this.inputValue = '';
      this.changeSubject.next(this);
    }
  }

  private removeTag(tag: string) {
    if (this.disabled) return;
    this.value = this.value.filter((t) => t !== tag);
    this.changeSubject.next(this);
  }

  public getValue(): string[] {
    return this.value;
  }

  public setValue(value: string[]): void {
    this.value = value;
  }
}
